import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../services/api.ts';

type SceneItem = {
  id: number;
  name?: string | null;
  status: string;
  start_frame_edited_url: string | null;
  created_at?: string | null;
  error_message?: string | null;
};

const ScenesLibrary: React.FC = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const {
    data: scenes = [],
    isLoading,
    error,
  } = useQuery<SceneItem[]>({
    queryKey: ['scenes-library'],
    queryFn: async () => {
      const res = await apiRequest('/api/dynamic-scene-generations');
      return res.data || [];
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  
  const [chooseImages, setChooseImages] = useState<{ id: string; url: string; generationId?: number; createdAt?: string }[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState<boolean>(false);
  
  // Two-card inputs (like AI Videos)
  const [startSource, setStartSource] = useState<'upload' | 'library'>('upload');
  const [startFile, setStartFile] = useState<File | null>(null);
  const [startLibraryUrl, setStartLibraryUrl] = useState<string | null>(null);
  const [secondSource, setSecondSource] = useState<'upload' | 'library'>('upload');
  const [secondFile, setSecondFile] = useState<File | null>(null);
  const [secondLibraryUrl, setSecondLibraryUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  // Dimensions
  const [selectedOrientation, setSelectedOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [selectedResolution, setSelectedResolution] = useState<string>('854x480');

  // View modal
  const [viewImageUrl, setViewImageUrl] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Preload user images for faster modal experience
    // No-op here; main list is handled by useQuery
  }, []);

  const handleDelete = async (id: number) => {
    const prev = queryClient.getQueryData<SceneItem[]>(['scenes-library']) || [];
    queryClient.setQueryData<SceneItem[]>(['scenes-library'], (cur) => (cur || []).filter((s) => s.id !== id));
    try {
      await apiRequest(`/api/dynamic-scene-generations/${id}`, { method: 'DELETE' });
    } catch (e) {
      // revert on failure
      queryClient.setQueryData(['scenes-library'], prev);
      alert('Failed to delete scene');
    }
  };

  const openCreateModal = async () => {
    setShowCreateModal(true);
    
    // reset two-card selections
    setStartSource('upload');
    setStartFile(null);
    setStartLibraryUrl(null);
    setSecondSource('upload');
    setSecondFile(null);
    setSecondLibraryUrl(null);
    setPrompt('');
    // prefetch library
    try {
      setIsLoadingLibrary(true);
      const imagesRes = await apiRequest('/api/user-images');
      setChooseImages(imagesRes.data || []);
    } catch {
      setChooseImages([]);
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const handleGenerate = async () => {
    // Validation: first image required
    if (startSource === 'upload' && !startFile) {
      alert('Please upload a first image');
      return;
    }
    if (startSource === 'library' && !startLibraryUrl) {
      alert('Please select a first image from library');
      return;
    }
    if (!prompt.trim()) {
      alert('Please enter a prompt');
      return;
    }

    setIsGenerating(true);
    closeCreateModal();

    // Optimistic placeholder card (update query cache only)
    const tempId = Date.now();
    const prev = queryClient.getQueryData<SceneItem[]>(['scenes-library']) || [];
    queryClient.setQueryData<SceneItem[]>(['scenes-library'], [
      {
        id: tempId,
        name: 'Generating...',
        status: 'generating',
        start_frame_edited_url: null,
        created_at: new Date().toISOString(),
      },
      ...prev,
    ]);

    try {
      let firstImage: string;
      if (startSource === 'upload' && startFile) {
        firstImage = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(startFile);
        });
      } else if (startSource === 'library' && startLibraryUrl) {
        firstImage = startLibraryUrl;
      } else {
        throw new Error('No first image selected');
      }

      let secondImage: string | null = null;
      if (secondSource === 'upload' && secondFile) {
        secondImage = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(secondFile);
        });
      } else if (secondSource === 'library' && secondLibraryUrl) {
        secondImage = secondLibraryUrl;
      }

      await apiRequest('/api/dynamic-scene-edit', {
        method: 'POST',
        body: JSON.stringify({
          image: firstImage,
          second_image: secondImage,
          prompt,
          orientation: selectedOrientation,
          resolution: selectedResolution,
        }),
      });
      // Refresh list to get real record with ID and URL
      await queryClient.invalidateQueries({ queryKey: ['scenes-library'] });
    } catch (e) {
      // On failure, remove temp card
      queryClient.setQueryData<SceneItem[]>(['scenes-library'], (cur) => (cur || []).filter((s) => s.id !== tempId));
      alert('Failed to generate scene');
    } finally {
      setIsGenerating(false);
    }
  };

  const headerTitle = useMemo(() => 'AI Scenes', []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      <div className="container mx-auto px-6 py-8">
          {/* Back */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back to Home</span>
            </button>
          </div>

          {/* Header - replicate AI Characters style */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-2">
              <h1 className="text-4xl font-bold text-gray-800">AI Scenes</h1>
              <button
                onClick={openCreateModal}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-3 hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:scale-105 shadow-lg"
                title="Create New Scene"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            <p className="text-gray-600">Your scenes library</p>
          </div>

          {/* Content */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-6">{(error as any)?.message || 'Failed to load scenes'}</div>
          )}

          {isLoading ? (
            <div className="min-h-[50vh] flex items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                <p className="text-gray-600">Loading scenes...</p>
              </div>
            </div>
          ) : scenes.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-12 text-center">
              <div className="text-6xl mb-4">🖼️</div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">No scenes yet</h2>
              <p className="text-gray-600 mb-6">Create your first scene by clicking the New button.</p>
              <button
                onClick={openCreateModal}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                + Create Scene
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {scenes.map((scene) => (
                <div key={scene.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                  <div
                    className="aspect-video bg-gray-100 flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      if (scene.start_frame_edited_url) setViewImageUrl(scene.start_frame_edited_url);
                    }}
                    title={scene.start_frame_edited_url ? 'View scene' : undefined}
                  >
                    {scene.start_frame_edited_url ? (
                      <img src={scene.start_frame_edited_url} alt={`Scene ${scene.id}`} className="w-full h-full object-contain" />
                    ) : (
                      <div className="text-gray-400 text-sm">{scene.status === 'generating' ? 'Generating...' : 'No preview'}</div>
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800 truncate max-w-[12rem]">{scene.name || `Scene #${scene.id}`}</div>
                      <div className="text-xs text-gray-500">{scene.created_at ? new Date(scene.created_at).toLocaleString() : ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {scene.status === 'generating' && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      )}
                      <button
                        onClick={() => handleDelete(scene.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => {
          if (modalRef.current && !modalRef.current.contains(e.target as Node)) setShowCreateModal(false);
        }}>
          <div ref={modalRef} className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Create Scene</h3>
              <button className="text-gray-500 hover:text-gray-700 text-2xl" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="p-5 overflow-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Image */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">First Image</h4>
                  <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-3">
                    <button
                      className={`py-2 px-4 rounded-md text-sm font-medium ${startSource === 'upload' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                      onClick={() => setStartSource('upload')}
                    >
                      Upload
                    </button>
                    <button
                      className={`py-2 px-4 rounded-md text-sm font-medium ${startSource === 'library' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                      onClick={() => setStartSource('library')}
                    >
                      From Library
                    </button>
                  </div>
                  {startSource === 'upload' ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 cursor-pointer" onClick={() => document.getElementById('scene-start-upload')?.click()}>
                      <input id="scene-start-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setStartFile(e.target.files?.[0] || null)} />
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">📷</span>
                      </div>
                      <p className="text-gray-600 mb-2 text-center">Click to upload first image</p>
                      {!!startFile && (
                        <div className="mt-4">
                          <img src={URL.createObjectURL(startFile)} alt="Selected first preview" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {isLoadingLibrary ? (
                        <div className="text-center text-gray-600 py-10">Loading images...</div>
                      ) : chooseImages.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">No images found</div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {chooseImages.map((img) => (
                            <div key={img.id} className={`border rounded-md overflow-hidden cursor-pointer ${startLibraryUrl === img.url ? 'border-blue-500' : 'border-gray-200'}`} onClick={() => setStartLibraryUrl(img.url)}>
                              <img src={img.url} alt={`Library ${img.generationId || ''}`} className="w-full h-28 object-contain" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Second Image (Optional) */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Second Image (Optional)</h4>
                  <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-3">
                    <button
                      className={`py-2 px-4 rounded-md text-sm font-medium ${secondSource === 'upload' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                      onClick={() => setSecondSource('upload')}
                    >
                      Upload
                    </button>
                    <button
                      className={`py-2 px-4 rounded-md text-sm font-medium ${secondSource === 'library' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                      onClick={() => setSecondSource('library')}
                    >
                      From Library
                    </button>
                  </div>
                  {secondSource === 'upload' ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-green-400 hover:bg-green-50 transition-all duration-200 cursor-pointer" onClick={() => document.getElementById('scene-second-upload')?.click()}>
                      <input id="scene-second-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setSecondFile(e.target.files?.[0] || null)} />
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">📷</span>
                      </div>
                      <p className="text-gray-600 mb-2 text-center">Click to upload second image</p>
                      {!!secondFile && (
                        <div className="mt-4">
                          <img src={URL.createObjectURL(secondFile)} alt="Selected second preview" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {isLoadingLibrary ? (
                        <div className="text-center text-gray-600 py-10">Loading images...</div>
                      ) : chooseImages.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">No images found</div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {chooseImages.map((img) => (
                            <div key={img.id} className={`border rounded-md overflow-hidden cursor-pointer ${secondLibraryUrl === img.url ? 'border-green-500' : 'border-gray-200'}`} onClick={() => setSecondLibraryUrl(img.url)}>
                              <img src={img.url} alt={`Library ${img.generationId || ''}`} className="w-full h-28 object-contain" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Orientation & Resolution */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <select
                    value={selectedOrientation}
                    onChange={(e) => setSelectedOrientation(e.target.value as 'landscape' | 'portrait')}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolution</label>
                  <select
                    value={selectedResolution}
                    onChange={(e) => setSelectedResolution(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {selectedOrientation === 'landscape' ? (
                      <>
                        <option value="854x480">854 x 480 (Default)</option>
                        <option value="1024x576">1024 x 576</option>
                        <option value="1280x720">1280 x 720</option>
                      </>
                    ) : (
                      <>
                        <option value="480x854">480 x 854 (Default)</option>
                        <option value="576x1024">576 x 1024</option>
                        <option value="720x1280">720 x 1280</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Selected previews (optional) */}
              {(startSource === 'library' && startLibraryUrl) && (
                <div className="mt-4">
                  <img src={startLibraryUrl} alt="Selected first image" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                </div>
              )}
              {(secondSource === 'library' && secondLibraryUrl) && (
                <div className="mt-2">
                  <img src={secondLibraryUrl} alt="Selected second image" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
                <input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the scene edits..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-3">
              <button className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className={`px-5 py-2 rounded-lg text-white font-semibold ${isGenerating ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`} onClick={handleGenerate} disabled={
                isGenerating ||
                !prompt.trim() ||
                (startSource === 'upload' && !startFile) ||
                (startSource === 'library' && !startLibraryUrl)
              }>
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Scene Modal */}
      {viewImageUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setViewImageUrl(null);
          }}
        >
          <div className="bg-white rounded-xl max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">View Scene</h3>
              <button onClick={() => setViewImageUrl(null)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto">
              <img src={viewImageUrl} alt="Scene preview" className="max-w-full max-h-[70vh] object-contain rounded" />
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  if (!viewImageUrl) return;
                  const link = document.createElement('a');
                  link.href = viewImageUrl;
                  link.download = `scene-${Date.now()}.png`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
              >
                Download
              </button>
              <button onClick={() => setViewImageUrl(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenesLibrary;


